import { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import type { LeafletEventHandlerFnMap } from 'leaflet';
import { useMap } from 'react-leaflet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faTimes } from '@fortawesome/pro-regular-svg-icons';

import {
  useAppDispatch,
  useAppSelector,
  labelsSelector,
  preferencesSelector,
  labelPositionsSelector,
  LabelStyle,
  LabelType,
  moveLabelInImage,
} from '../../../../store';

import eventBus from '../../../../eventBus';
import {
  toSelectionPoint,
  appendSelectionPoint,
  isWithinSelectionLasso,
  LabelSelectionPoint,
} from '../../../../utils/labels';

import { EditLabel } from '../../Sidebar/Edit/EditLabel';
import { MapEventHandler } from '../../../Map/MapEventHandler';
import rootElement from '../../../../rootElement';

import { Marker } from './Marker';
import './LabelEditing.scss';

export interface LabelPreviewType {
  labelId: string;
  newLabelId?: string;
  name: string;
  style: LabelStyle;
  rotation?: number;
}

export function LabelEditing() {
  const dispatch = useAppDispatch();
  const map = useMap();
  const preferences = useAppSelector(preferencesSelector);
  const labels = useAppSelector(labelsSelector);
  const labelPositions = useAppSelector(labelPositionsSelector);
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const [labelPreview, setLabelPreview] = useState<LabelPreviewType | null>(null);

  const [editedLabel, setEditedLabel] = useState<LabelType|null|undefined>(null);
  const [editedLabelPosition, setEditedLabelPosition] = useState<LabelSelectionPoint | null>(null);

  const drawerRef = useRef<HTMLElement>(null);
  const selectionPathRef = useRef<LabelSelectionPoint[] | null>(null);
  const suppressNextClickRef = useRef(false);
  const draggingState = useRef<{
    marker: L.Marker | null;
    offset: { x: number; y: number } | null;
  }>({ marker: null, offset: null });
  const selectionGestureActiveRef = useRef(false);
  const [selectionLasso, setSelectionLasso] = useState<LabelSelectionPoint[] | null>(null);

  const closeEditForm = useCallback(() => {
    setLabelPreview(null);
    setEditedLabel(null);
    setSelectedLabelIds([]);
  }, []);

  useEffect(() => {
    if (editedLabel) {
      const primaryFocusTarget = drawerRef.current?.querySelector<HTMLElement>('[data-label-editor-primary-focus="true"]');
      if (primaryFocusTarget) {
        primaryFocusTarget.focus();
      } else {
        drawerRef.current?.focus();
      }
    }
  }, [editedLabel]);

  const updateSelectionFromPointer = useCallback((event: MouseEvent) => {
    const currentPath = selectionPathRef.current;
    if (!currentPath) {
      return;
    }

    const nextPath = appendSelectionPoint(currentPath, toSelectionPoint(map.mouseEventToLatLng(event)));
    selectionPathRef.current = nextPath;
    setSelectionLasso(nextPath);
  }, [map]);

  const finishSelectionGesture = useCallback(function finishSelectionGesture(event: MouseEvent) {
    setEditedLabelPosition(null);
    const currentPath = selectionPathRef.current;
    if (!currentPath) {
      return;
    }

    const lasso = appendSelectionPoint(currentPath, toSelectionPoint(map.mouseEventToLatLng(event)));
    const nextSelectedIds = Object.entries(labelPositions)
      .filter((entry): entry is [string, LabelSelectionPoint] => Boolean(entry[1]))
      .filter(([, position]) => isWithinSelectionLasso(position, lasso))
      .map(([labelId]) => labelId);

    selectionPathRef.current = null;
    suppressNextClickRef.current = true;
    setSelectionLasso(null);
    window.removeEventListener('mousemove', updateSelectionFromPointer);
    window.removeEventListener('mouseup', finishSelectionGesture);
    map.dragging?.enable?.();
    map.boxZoom?.enable?.();
    selectionGestureActiveRef.current = false;
    setLabelPreview(null);
    setSelectedLabelIds(nextSelectedIds);
    setEditedLabel(labels.find((label) => label.id === nextSelectedIds[0]));
  }, [labelPositions, map, updateSelectionFromPointer, labels]);

  // Select all with ctrl+a
  useEffect(() => {
    const selectAll = () => {
      setEditedLabel(labels.find((label) => label.id === Object.keys(labelPositions)[0]));
      setSelectedLabelIds(Object.keys(labelPositions));
    };

    eventBus.on('select-all', selectAll);
    return () => {
      eventBus.off('select-all', selectAll);
    };
  }, [labels, labelPositions]);

  useEffect(() => () => {
    selectionPathRef.current = null;
    window.removeEventListener('mousemove', updateSelectionFromPointer);
    window.removeEventListener('mouseup', finishSelectionGesture);
    selectionGestureActiveRef.current = false;
    map.dragging?.enable?.();
    map.boxZoom?.enable?.();
  }, [finishSelectionGesture, map, updateSelectionFromPointer]);

  const moveMarker = useCallback((e: L.LeafletMouseEvent) => {
    const { marker, offset } = draggingState.current;
    if (marker && offset) {
      const cursorPoint = map.mouseEventToContainerPoint(e.originalEvent);
      marker.setLatLng(
        map.containerPointToLatLng([
          cursorPoint.x - offset.x,
          cursorPoint.y - offset.y,
        ])
      );
    }
  }, [map]);

  const mapHandlers = useMemo<LeafletEventHandlerFnMap>(() => ({
    mousedown: (e) => {
      if (!e.originalEvent?.shiftKey) return;

      const start = toSelectionPoint(e.latlng);
      selectionPathRef.current = [start];
      suppressNextClickRef.current = false;
      setSelectionLasso([start]);
      window.removeEventListener('mousemove', updateSelectionFromPointer);
      window.removeEventListener('mouseup', finishSelectionGesture);
      window.addEventListener('mousemove', updateSelectionFromPointer);
      window.addEventListener('mouseup', finishSelectionGesture);
      map.dragging?.disable?.();
      map.boxZoom?.disable?.();
      selectionGestureActiveRef.current = true;
      L.DomEvent.stopPropagation(e);
    },
    click: (e) => {
      if (suppressNextClickRef.current) {
        suppressNextClickRef.current = false;
        return;
      }
      if (editedLabel) {
        setEditedLabelPosition(null);
        closeEditForm();
      } else {
        const { lat:x, lng:y } = e.latlng;
        setEditedLabelPosition({ x, y, rotation: preferences.defaultLabelRotation ?? 0 });
        setEditedLabel({ id: 'newLabel', name: '' });
        setSelectedLabelIds(['newLabel']);
      }
    },
    dragstart: (e) => {
      e.target._container.classList.add('dragging-map');
    },
    dragend: (e) => {
      e.target._container.classList.remove('dragging-map');
    },
  }), [finishSelectionGesture, editedLabel, map, updateSelectionFromPointer, closeEditForm, preferences.defaultLabelRotation]);

  const markerHandlers = useCallback<(label:LabelType) => LeafletEventHandlerFnMap>((label:LabelType) => {
    return {
      click: (e) => {
        L.DomEvent.stopPropagation(e);
      },
      mouseup: (e) => {
        if (selectionGestureActiveRef.current && e.originalEvent?.shiftKey) {
          L.DomEvent.stopPropagation(e);
          return;
        }
        if (draggingState.current.marker) {
          const latlng = draggingState.current.marker.getLatLng();
          if (label.id !== 'newLabel') {
            dispatch(moveLabelInImage({ id: label.id, x: latlng.lat, y: latlng.lng }));
          }
          draggingState.current = { marker: null, offset: null };
          map.off('mousemove', moveMarker);
          map.dragging.enable();
          if (label.id === 'newLabel') {
            setEditedLabelPosition({
              ...editedLabelPosition,
              x: latlng.lat,
              y: latlng.lng,
            });
          }
        } else {
          const modifierKeyPressed = Boolean(
            e.originalEvent?.ctrlKey || e.originalEvent?.metaKey || e.originalEvent?.shiftKey,
          );

          if (modifierKeyPressed) {
            const alreadySelected = selectedLabelIds.includes(label.id);
            const nextSelectedIds = alreadySelected
              ? selectedLabelIds.filter((selectedId) => selectedId !== label.id)
              : [...selectedLabelIds, label.id];

            setSelectedLabelIds(nextSelectedIds);

            if (nextSelectedIds.length === 0) {
              setEditedLabel(null);
            } else if (alreadySelected && editedLabel?.id === label.id) {
              setEditedLabel(labels.find((l) => l.id === nextSelectedIds[0]));
            } else if (!editedLabel?.id) {
              setEditedLabel(labels.find((l) => l.id === label.id));
            }
          } else if (editedLabel?.id === label.id && selectedLabelIds.length <= 1) {
            setLabelPreview(null);
            setSelectedLabelIds([]);
            setEditedLabel(null);
          } else {
            setLabelPreview(null);
            const group = preferences?.labelSelectionGroups?.find((g) => g.labelIds.includes(label.id));
            // Select whole group on the first click on a group member
            const selectGroup = group && !group.labelIds.some((id) => selectedLabelIds.includes(id));
            if (selectGroup) {
              setSelectedLabelIds(group.labelIds);
            } else {
              setSelectedLabelIds([label.id]);
            }
            setEditedLabel(labels.find((l) => l.id === label.id));
            setEditedLabelPosition(labelPositions[label.id] ?? null);
          }
        }
      },
      mousemove: (e) => {
        if (e.originalEvent.buttons !== 1 || e.originalEvent.shiftKey) return;
        if (!draggingState.current.marker) {
          const marker = e.target as L.Marker;
          const cursorPoint = map.mouseEventToContainerPoint(e.originalEvent);
          const markerPoint = map.latLngToContainerPoint(marker.getLatLng());

          draggingState.current = {
            marker,
            offset: {
              x: cursorPoint.x - markerPoint.x,
              y: cursorPoint.y - markerPoint.y,
            },
          };

          map.dragging.disable();
          map.on('mousemove', moveMarker);
        }
      },
    };
  }, [dispatch, editedLabel, labels, map, selectedLabelIds, moveMarker, preferences?.labelSelectionGroups, editedLabelPosition, labelPositions]);

  const mapContainer = map.getContainer?.() ?? null;
  const selectionSvgPoints = selectionLasso?.map((point) => {
    const containerPoint = map.latLngToContainerPoint([point.x, point.y]);
    return `${containerPoint.x},${containerPoint.y}`;
  }).join(' ') ?? '';

  const drawer = editedLabel && (
    <aside
      aria-label="Label editor"
      ref={drawerRef}
      className="theme-sidebar label-editor-drawer"
      tabIndex={-1}
      onKeyUp={(e) => {
        if (e.key === 'Escape') {
          closeEditForm();
        }
      }}
    >
      <header className="theme-sidebar__header">
        <div className="theme-sidebar__title">
          <FontAwesomeIcon icon={faTag} />
          <h3>Label Editor</h3>
        </div>
        <button
          type="button"
          aria-label="Close label editor"
          onClick={closeEditForm}
          className="theme-sidebar__close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </header>

      <div className="theme-sidebar__content label-editor-drawer__content">
        <EditLabel
          marker={editedLabel}
          position={editedLabelPosition ?? labelPositions[editedLabel.id]}
          onPreviewChange={setLabelPreview}
          onClose={closeEditForm}
          selectedLabelIds={selectedLabelIds}
        />
      </div>

      <footer id="edit-label-footer" className="theme-sidebar__footer label-editor-drawer__footer">
      </footer>
    </aside>
  );
  const portalTarget = rootElement || document.body;

  return (
    <>
      <MapEventHandler enabled={true} handlers={mapHandlers} />
      {editedLabel?.id === 'newLabel' && editedLabelPosition && (
        <Marker
          key={editedLabel.id}
          label={editedLabel}
          editedLabel={editedLabel}
          position={editedLabelPosition}
          isSelected={true}
          labelPreview={labelPreview}
          handlers={markerHandlers(editedLabel)}
          defaultRotation={preferences.defaultLabelRotation}
        />
      )}
      {labels.filter((l) => labelPositions[l.id]).map((label) => (
        <Marker
          key={label.id}
          label={label}
          editedLabel={editedLabel}
          position={labelPositions[label.id]}
          isSelected={selectedLabelIds.includes(label.id)}
          labelPreview={labelPreview}
          handlers={markerHandlers(label)}
          defaultRotation={preferences.defaultLabelRotation}
        />
      ))}
      {selectionLasso && selectionLasso.length >= 2 && mapContainer && createPortal(
        <svg
          aria-hidden="true"
          className="label-selection-lasso"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            overflow: 'visible',
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        >
          {selectionLasso.length >= 3 ? (
            <polygon
              data-testid="selection-lasso-area"
              points={selectionSvgPoints}
              fill="rgba(26, 115, 232, 0.12)"
              stroke="#1a73e8"
              strokeWidth="1.5"
              strokeDasharray="6 4"
            />
          ) : (
            <polyline
              data-testid="selection-lasso-line"
              points={selectionSvgPoints}
              fill="none"
              stroke="#1a73e8"
              strokeWidth="1.5"
              strokeDasharray="6 4"
            />
          )}
        </svg>,
        mapContainer,
      )}
      {drawer ? createPortal(drawer, portalTarget) : null}
    </>
  );
}
